import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faCalendarAlt, faClock, faTv, 
  faGlobe, faInfoCircle, faPlay, faPlus, 
  faStar, faShareAlt, faBookmark, faCheck, faArrowLeft,
  faLayerGroup, faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import TrailerModal from './TrailerModal';
import SeriesModal from './SeriesModal';
import { Link } from 'react-router-dom';

// ============================================================
//  GIFT-CODE AD-FREE CHECK (same as Navbar)
// ============================================================
const ACTIVE_GIFT_CODES = ['SHREK', 'GARFIELD', 'TOMANDJERRY'];
const REVOKED_CODES = [''];

const isUserAdFree = () => {
  const redeemedCode = localStorage.getItem('adFreeCode');
  if (!redeemedCode) return false;
  return !REVOKED_CODES.includes(redeemedCode);
};

// Language code to full name mapping
const languageMap = {
  en: 'English',
  ru: 'Russian',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  pt: 'Portuguese',
  ar: 'Arabic',
  tr: 'Turkish',
  th: 'Thai',
  nl: 'Dutch',
  sv: 'Swedish',
  pl: 'Polish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  el: 'Greek',
  cs: 'Czech',
  hu: 'Hungarian',
  he: 'Hebrew',
  id: 'Indonesian',
  ms: 'Malay',
  vi: 'Vietnamese',
  tl: 'Filipino',
  fa: 'Persian',
  bn: 'Bengali',
  ta: 'Tamil',
  te: 'Telugu',
  mr: 'Marathi',
  kn: 'Kannada',
  ml: 'Malayalam',
  ur: 'Urdu',
  uk: 'Ukrainian',
  ro: 'Romanian',
  sk: 'Slovak',
  bg: 'Bulgarian',
  hr: 'Croatian',
  sr: 'Serbian',
  sl: 'Slovenian',
  et: 'Estonian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  ka: 'Georgian',
  hy: 'Armenian',
  sq: 'Albanian',
  mk: 'Macedonian',
};

const getLanguageName = (code) => {
  return languageMap[code] || code.toUpperCase();
};

// Map our language codes to TMDB locale codes
const getTmdbLanguage = (lang) => {
  const map = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
  };
  return map[lang] || 'en-US';
};

// ================== MODAL TRANSLATIONS ==================
const modalTranslations = {
  en: {
    noOverview: "No overview available.",
    partOfCollection: "Part of a collection",
    collectionDescription: "These films may share a continuing storyline.",
    topCast: "Top Cast",
    supportingCast: "Supporting Cast",
    similarMovies: "Similar Movies",
    filmography: "Filmography",
    movies: "Movies",
    tvShows: "TV Shows",
    noFilmography: (type) => `No ${type} found with posters and at least 50 votes`,
    loadingFilmography: "Loading filmography...",
    unforgettable: "An unforgettable cinematic experience",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    youAreHere: "You are here",
    back: "Back",
  },
  fr: {
    noOverview: "Aucun aperçu disponible.",
    partOfCollection: "Fait partie d'une collection",
    collectionDescription: "Ces films peuvent partager une intrigue continue.",
    topCast: "Distribution principale",
    supportingCast: "Distribution secondaire",
    similarMovies: "Films similaires",
    filmography: "Filmographie",
    movies: "Films",
    tvShows: "Séries TV",
    noFilmography: (type) => `Aucun ${type} trouvé avec des affiches et au moins 50 votes`,
    loadingFilmography: "Chargement de la filmographie...",
    unforgettable: "Une expérience cinématographique inoubliable",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    youAreHere: "Vous êtes ici",
    back: "Retour",
  },
  es: {
    noOverview: "No hay resumen disponible.",
    partOfCollection: "Parte de una colección",
    collectionDescription: "Estas películas pueden compartir una historia continua.",
    topCast: "Reparto principal",
    supportingCast: "Reparto secundario",
    similarMovies: "Películas similares",
    filmography: "Filmografía",
    movies: "Películas",
    tvShows: "Series TV",
    noFilmography: (type) => `No se encontraron ${type} con pósteres y al menos 50 votos`,
    loadingFilmography: "Cargando filmografía...",
    unforgettable: "Una experiencia cinematográfica inolvidable",
    votes: (count) => `(${count.toLocaleString()} votos)`,
    youAreHere: "Estás aquí",
    back: "Volver",
  },
  it: {
    noOverview: "Nessuna panoramica disponibile.",
    partOfCollection: "Parte di una collezione",
    collectionDescription: "Questi film potrebbero condividere una trama continua.",
    topCast: "Cast principale",
    supportingCast: "Cast di supporto",
    similarMovies: "Film simili",
    filmography: "Filmografia",
    movies: "Film",
    tvShows: "Serie TV",
    noFilmography: (type) => `Nessun ${type} trovato con poster e almeno 50 voti`,
    loadingFilmography: "Caricamento filmografia...",
    unforgettable: "Un'esperienza cinematografica indimenticabile",
    votes: (count) => `(${count.toLocaleString()} voti)`,
    youAreHere: "Sei qui",
    back: "Indietro",
  },
  nl: {
    noOverview: "Geen overzicht beschikbaar.",
    partOfCollection: "Onderdeel van een collectie",
    collectionDescription: "Deze films kunnen een doorlopend verhaal delen.",
    topCast: "Hoofdcast",
    supportingCast: "Ondersteunende cast",
    similarMovies: "Soortgelijke films",
    filmography: "Filmografie",
    movies: "Films",
    tvShows: "TV-series",
    noFilmography: (type) => `Geen ${type} gevonden met posters en minimaal 50 stemmen`,
    loadingFilmography: "Filmografie laden...",
    unforgettable: "Een onvergetelijke filmervaring",
    votes: (count) => `(${count.toLocaleString()} stemmen)`,
    youAreHere: "Je bent hier",
    back: "Terug",
  },
};

// ================== COMPONENT ==================
const MoviesModal = ({ movie, onClose, onUpdate, onBack, history }) => {
  const [currentMovie, setCurrentMovie] = useState(movie);
  const [credits, setCredits] = useState(null);
  const [videos, setVideos] = useState(null);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [trailerKey, setTrailerKey] = useState(null);
  const [isAdded, setIsAdded] = useState(false);
  const [recommendedMovies, setRecommendedMovies] = useState([]);
  const [localHistory, setLocalHistory] = useState(history || []);
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorFilmography, setActorFilmography] = useState(null);
  const [contentFilter, setContentFilter] = useState('movies');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTVShow, setSelectedTVShow] = useState(null);
  const [collection, setCollection] = useState(null);
  const [showCollection, setShowCollection] = useState(false);
  const [collectionScrollIndex, setCollectionScrollIndex] = useState(0);
  const [showBlinkingEffect, setShowBlinkingEffect] = useState(false);
  
  // Language state
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  // Rate limiting state
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  
  const apiKey = '57240db50c2008e78c261e1a934627e4';
  const adLink = 'https://amrox.org/4/0eb5c90380ccc36ce2bdf72e653ec6bb';

  const isInitialMount = useRef(true);
  const modalContentRef = useRef(null);
  const actorModalRef = useRef(null);
  const collectionCarouselRef = useRef(null);
  const actorFallbackImage = 'https://i.ibb.co/Lkf9x3W/hsaSDasa.png';
  const cooldownIntervalRef = useRef(null);
  const requestTimestampsRef = useRef([]);
  const blinkIntervalRef = useRef(null);

  // Translation helper
  const t = (key, ...args) => {
    const langData = modalTranslations[language] || modalTranslations.en;
    const keys = key.split('.');
    let value = langData;
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // fallback to English
        const enData = modalTranslations.en;
        let fallback = enData;
        for (const fk of keys) {
          if (fallback && fallback[fk] !== undefined) {
            fallback = fallback[fk];
          } else {
            return key; // fallback to key itself
          }
        }
        if (typeof fallback === 'function') {
          return fallback(...args);
        }
        return fallback;
      }
    }
    if (typeof value === 'function') {
      return value(...args);
    }
    return value;
  };

  // Function to format actor names for mobile
  const formatActorName = (name) => {
    if (!isMobile) return name;
    
    const parts = name.split(' ');
    if (parts.length === 1) {
      return name.length > 16 ? `${name.substring(0, 16)}.` : name;
    }
    
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    
    const truncatedFirstName = firstName.length > 8 ? `${firstName.substring(0, 8)}.` : firstName;
    const truncatedLastName = lastName.length > 8 ? `${lastName.substring(0, 8)}.` : lastName;
    
    return `${truncatedFirstName} ${truncatedLastName}`;
  };

  useEffect(() => {
    // Check if user has previously clicked the collection
    const hasClickedCollection = localStorage.getItem('collectionCapsuleClicked');
    setShowBlinkingEffect(!hasClickedCollection);
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setCurrentMovie(movie);
      setLocalHistory([]);
      setSelectedActor(null);
      setCredits(null);
      setVideos(null);
      setTrailerKey(null);
      setRecommendedMovies([]);
      setShowTrailerModal(false);
      setActorFilmography(null);
      setContentFilter('movies');
      setSelectedTVShow(null);
      setCollection(null);
      setShowCollection(false);
      setCollectionScrollIndex(0);
    }
  }, [movie]);

  // Rate limiting check
  const checkRateLimit = () => {
    if (isRateLimited) return false;
    
    const now = Date.now();
    const tenSecondsAgo = now - 10000;
    
    // Filter requests from the last 10 seconds
    const recentRequests = requestTimestampsRef.current.filter(ts => ts > tenSecondsAgo);
    
    // If 22 or more requests in last 10 seconds, activate cooldown
    if (recentRequests.length >= 25) {
      setIsRateLimited(true);
      setCooldownSeconds(8);
      
      // Start countdown
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(cooldownIntervalRef.current);
            setIsRateLimited(false);
            requestTimestampsRef.current = [];
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return false;
    }
    
    // Track this request
    requestTimestampsRef.current.push(now);
    return true;
  };

  // ---- Main data loading function (with language) ----
  const loadMovieData = async (movieId, lang) => {
    if (!movieId) return;
    if (!checkRateLimit()) return;

    try {
      // 1. Fetch movie details in English (for title) and in user language
      const [enRes, userRes] = await Promise.all([
        fetch(`https://api.themoviedb.org/3/movie/${movieId}?api_key=${apiKey}&language=en-US`),
        fetch(`https://api.themoviedb.org/3/movie/${movieId}?api_key=${apiKey}&language=${getTmdbLanguage(lang)}`)
      ]);
      const enData = await enRes.json();
      const userData = await userRes.json();

      // Merge: keep English title, use user language for everything else
      const mergedMovie = {
        ...userData,
        title: enData.title,
        original_title: enData.original_title || enData.title,
      };
      setCurrentMovie(mergedMovie);

      // 2. Fetch credits, videos, recommendations
      //    - Videos: always use English (en-US) to guarantee trailer availability
      //    - Credits & recommendations: use user language for localized names/titles
      const [creditsRes, videosRes, recRes] = await Promise.all([
        fetch(`https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${apiKey}&language=${getTmdbLanguage(lang)}`),
        fetch(`https://api.themoviedb.org/3/movie/${movieId}/videos?api_key=${apiKey}&language=en-US`), // <-- FIXED: always English
        fetch(`https://api.themoviedb.org/3/movie/${movieId}/recommendations?api_key=${apiKey}&language=${getTmdbLanguage(lang)}&page=1`)
      ]);

      const creditsData = await creditsRes.json();
      const videosData = await videosRes.json();
      const recData = await recRes.json();

      setCredits(creditsData);
      setVideos(videosData);
      setRecommendedMovies(recData.results || []);

      const trailer = videosData.results?.find(video => video.type === 'Trailer');
      if (trailer) setTrailerKey(trailer.key);

      // 3. Fetch collection (if exists) in user language
      if (mergedMovie.belongs_to_collection) {
        const collectionRes = await fetch(
          `https://api.themoviedb.org/3/collection/${mergedMovie.belongs_to_collection.id}?api_key=${apiKey}&language=${getTmdbLanguage(lang)}`
        );
        const collectionData = await collectionRes.json();
        const validMovies = collectionData.parts?.filter(m => m.poster_path && m.vote_count > 0) || [];
        if (validMovies.length > 1) {
          setCollection({ ...collectionData, parts: validMovies });
        } else {
          setCollection(null);
        }
      }
    } catch (error) {
      console.error('Error loading movie data:', error);
    }
  };

  // Trigger data load when movie or language changes
  useEffect(() => {
    if (currentMovie && currentMovie.id) {
      loadMovieData(currentMovie.id, language);
    }
  }, [currentMovie?.id, language]);

  // Listen for language changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        const newLang = e.newValue || 'en';
        setLanguage(newLang);
        if (currentMovie && currentMovie.id) {
          loadMovieData(currentMovie.id, newLang);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentMovie]);

  // Actor filmography (with language)
  useEffect(() => {
    if (!selectedActor) return;
    
    const fetchFilmography = async () => {
      if (!checkRateLimit()) return;
      try {
        const response = await fetch(
          `https://api.themoviedb.org/3/person/${selectedActor.id}/combined_credits?api_key=${apiKey}&language=${getTmdbLanguage(language)}`
        );
        const data = await response.json();
        setActorFilmography(data);
      } catch (error) {
        console.error('Error fetching actor filmography:', error);
      }
    };
    fetchFilmography();
  }, [selectedActor, apiKey, language]);

  useEffect(() => {
    // Start blinking effect when collection is available and should blink
    if (collection && showBlinkingEffect) {
      blinkIntervalRef.current = setInterval(() => {
        const element = document.querySelector('.collection-section.capsule');
        if (element) {
          element.classList.toggle('blinking');
        }
      }, 1000);
    }

    // Clean up interval when component unmounts or collection changes
    return () => {
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
      }
    };
  }, [collection, showBlinkingEffect]);

  useEffect(() => {
    if (currentMovie) {
      document.body.style.overflow = 'hidden';
      const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
      const isMovieAlreadyAdded = watchlist.some(item => 
        item.id === currentMovie.id && item.type === 'movie'
      );
      setIsAdded(isMovieAlreadyAdded);
      
      // Dispatch custom event to notify Navbar
      const movieOpenedEvent = new CustomEvent('movieOpened', { detail: currentMovie });
      window.dispatchEvent(movieOpenedEvent);
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [currentMovie]);

  useEffect(() => {
    scrollToTop();
  }, [currentMovie]);

  useEffect(() => {
    scrollToTop();
  }, [selectedActor]);

  useEffect(() => {
    if (selectedActor) scrollToTop();
  }, [contentFilter]);

  useEffect(() => {
    setCollectionScrollIndex(0);
  }, [collection, showCollection]);

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
    if (actorModalRef.current) {
      actorModalRef.current.scrollTop = 0;
    }
  };

  const handleAddToWatchlist = () => {
    if (!currentMovie) return;
    
    const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    const isMovieAlreadyAdded = watchlist.some(item => 
      item.id === currentMovie.id && item.type === 'movie'
    );
    
    let updatedWatchlist;
    
    if (isMovieAlreadyAdded) {
      updatedWatchlist = watchlist.filter(item => 
        !(item.id === currentMovie.id && item.type === 'movie')
      );
    } else {
      const movieData = {
        id: currentMovie.id,
        title: currentMovie.title,
        poster_path: currentMovie.poster_path,
        release_date: currentMovie.release_date,
        vote_average: currentMovie.vote_average,
        type: 'movie'
      };
      updatedWatchlist = [...watchlist, movieData];
    }
    
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    setIsAdded(!isMovieAlreadyAdded);
    if (onUpdate) onUpdate();
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('movie-modal')) {
      onClose();
    }
  };

  const createStarRating = (rating, size = 'normal') => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    
    let starsHTML = [];
    const starSize = size === 'small' ? '14px' : '19px';
    
    for (let i = 0; i < fullStars; i++) {
      starsHTML.push(
        <div className="star" key={`full-${i}`} style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    if (partialStar > 0) {
      starsHTML.push(
        <div className="star" key="partial" style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      starsHTML.push(
        <div className="star" key={`empty-${i}`} style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    return starsHTML;
  };

  const handleRecommendationClick = (recMovie) => {
    if (isRateLimited) return;
    if (!checkRateLimit()) return;
    
    setLocalHistory(prev => [...prev, { 
      type: 'movie',
      movie: currentMovie,
      actor: selectedActor
    }]);
    setCurrentMovie(recMovie);
    setSelectedActor(null);
    
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  const handleCollectionItemClick = (collectionMovie) => {
    if (isRateLimited) return;
    if (!checkRateLimit()) return;
    
    setLocalHistory(prev => [...prev, { 
      type: 'movie-from-collection',
      movie: currentMovie,
      actor: selectedActor
    }]);
    setCurrentMovie(collectionMovie);
    setSelectedActor(null);
    setShowCollection(false);
    
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  const handleBackClick = () => {
    if (onBack && localHistory.length === 0) {
      onBack();
    } else if (localHistory.length > 0) {
      const prevState = localHistory[localHistory.length - 1];
      
      if (prevState.type === 'movie-from-actor') {
        setCurrentMovie(prevState.movie);
        setSelectedActor(prevState.actor);
      } 
      else if (prevState.type === 'movie') {
        setCurrentMovie(prevState.movie);
        setSelectedActor(prevState.actor);
      }
      else if (prevState.type === 'actor') {
        setSelectedActor(prevState.actor);
      }
      else if (prevState.type === 'tv-show-from-actor') {
        setSelectedActor(prevState.actor);
        setSelectedTVShow(null);
      }
      else if (prevState.type === 'movie-from-collection') {
        setCurrentMovie(prevState.movie);
        setSelectedActor(prevState.actor);
      }
      
      setLocalHistory(prev => prev.slice(0, -1));
    }
  };

  const handleActorClick = (person) => {
    if (isRateLimited) return;
    if (!checkRateLimit()) return;
    
    setLocalHistory(prev => [...prev, { 
      type: 'actor',
      movie: currentMovie,
      actor: selectedActor
    }]);
    setSelectedActor(person);
  };

  const handleCloseActorModal = () => {
    setLocalHistory(prev => {
      if (prev.length > 0) {
        return prev.slice(0, -1);
      }
      return prev;
    });
    setSelectedActor(null);
    setActorFilmography(null);
    setContentFilter('movies');
  };

  const handleFilmographyItemClick = (item) => {
    if (isRateLimited) return;
    if (!checkRateLimit()) return;
    
    if (item.media_type === 'movie') {
      setLocalHistory(prev => [...prev, { 
        type: 'movie-from-actor',
        movie: currentMovie,
        actor: selectedActor
      }]);
      setCurrentMovie(item);
      setSelectedActor(null);
    }
    else if (item.media_type === 'tv') {
      handleTVShowClick(item);
    }
  };

  const handleTVShowClick = (tvShow) => {
    if (isRateLimited) return;
    if (!checkRateLimit()) return;
    
    setLocalHistory(prev => [...prev, { 
      type: 'tv-show-from-actor',
      movie: currentMovie,
      actor: selectedActor
    }]);
    setSelectedTVShow(tvShow.id);
    setSelectedActor(null);
  };

  const handleCloseTVModal = () => {
    setSelectedTVShow(null);
    const prevState = localHistory[localHistory.length - 1];
    if (prevState && prevState.type === 'tv-show-from-actor') {
      setSelectedActor(prevState.actor);
    }
    setLocalHistory(prev => prev.slice(0, -1));
  };

  const filteredFilmography = () => {
    if (!actorFilmography) return [];
    
    const uniqueItemsMap = new Map();
    
    actorFilmography.cast.forEach(item => {
      if (
        (contentFilter === 'movies' && 
         item.media_type === 'movie' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80) ||
        (contentFilter === 'tv' && 
         item.media_type === 'tv' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80 &&
         !item.genre_ids?.some(id => [10763, 10767, 10764].includes(id)))
      ) {
        const uniqueKey = `${item.id}-${item.character || 'default'}`;
        if (!uniqueItemsMap.has(uniqueKey)) {
          uniqueItemsMap.set(uniqueKey, item);
        }
      }
    });
    
    const filtered = Array.from(uniqueItemsMap.values());
    
    filtered.sort((a, b) => {
      const dateA = a.release_date || a.first_air_date;
      const dateB = b.release_date || b.first_air_date;
      
      if (dateA && dateB) {
        return new Date(dateB) - new Date(dateA);
      }
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      return 0;
    });
    
    return filtered;
  };

  const scrollCollection = (direction) => {
    if (!collection) return;
    
    const itemsPerPage = isMobile ? 3 : 5;
    const maxIndex = Math.ceil(collection.parts.length / itemsPerPage) - 1;
    
    if (direction === 'next') {
      setCollectionScrollIndex(prev => Math.min(prev + 1, maxIndex));
    } else {
      setCollectionScrollIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const handleCollectionClick = () => {
    localStorage.setItem('collectionCapsuleClicked', 'true');
    setShowBlinkingEffect(false);
    setShowCollection(true);
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
    }
  };

  // ========== HANDLE PLAY BUTTON CLICK (ad-free aware) ==========
  const handlePlayClick = () => {
    // Open the ad link only if the user is NOT ad-free
    if (!isUserAdFree()) {
      window.open(adLink, '_blank');
    }
    // Otherwise, do nothing – the <Link> will navigate normally
  };

  if (!currentMovie) return null;

  const backdropPath = currentMovie.backdrop_path 
    ? `https://image.tmdb.org/t/p/w780${currentMovie.backdrop_path}`
    : 'https://via.placeholder.com/1920x1080?text=No+Backdrop';
  
  const posterPath = currentMovie.poster_path 
    ? `https://image.tmdb.org/t/p/w500${currentMovie.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';
  
  const topCastCount = isMobile ? 4 : 5;
  const supportingCastCount = isMobile ? 4 : 5;
  const recCount = isMobile ? 6 : 10;
  const recMoviesToShow = recommendedMovies.slice(0, recCount);
  
  // Collection carousel calculations
  const itemsPerPage = isMobile ? 3 : 5;
  const collectionItems = collection ? collection.parts
    .sort((a, b) => new Date(a.release_date) - new Date(b.release_date)) : [];
  const maxScrollIndex = collection ? Math.ceil(collectionItems.length / itemsPerPage) - 1 : 0;
  const canScrollLeft = collectionScrollIndex > 0;
  const canScrollRight = collectionScrollIndex < maxScrollIndex;

  return (
    <div className="movie-modal active" onClick={handleBackdropClick}>
      <style>
        {`
          @keyframes blink {
            0% { box-shadow: 0 0 0 0 rgba(30, 255, 0, 0.78); }
            50% { box-shadow: 0 0 0 10px rgba(229, 9, 20, 0); }
            100% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0); }
          }
          
          .blinking {
            animation: blink 2s infinite;
          }
        `}
      </style>
      
      {/* Rate Limit Overlay */}
      {isRateLimited && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          flexDirection: 'column',
          color: 'white'
        }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Please slow down
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            color: '#e50914'
          }}>
            {cooldownSeconds}s
          </div>
          <p style={{ marginTop: '1rem', textAlign: 'center', maxWidth: '300px' }}>
            You've been making requests too quickly. Please wait a moment before continuing.
          </p>
        </div>
      )}
      
      <div 
        className="modal-backdrop" 
        style={{ backgroundImage: `url(${backdropPath})` }}
      ></div>
      <div className="modal-overlay"></div>
      <div 
        className="modal-content"
        ref={modalContentRef}
        style={{ overflowY: 'auto' }}
      >
        <div className="modal-header">
          {(localHistory.length > 0 || onBack) && (
            <div 
              className="back-modal" 
              onClick={handleBackClick}
              style={{
                position: 'absolute',
                top: '30px',
                right: '90px',
                background: 'rgba(255, 255, 255, 0.15)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 10,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ color: 'white', fontSize: '24px' }} />
            </div>
          )}
          <div className="close-modal" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className="modal-body">
          <div className="modal-poster">
            <img id="modalPoster" src={posterPath} alt={currentMovie.title} style={{ 
              width: '100%',
              height: 'auto',
              aspectRatio: '2/3',
              objectFit: 'cover',
              borderRadius: '8px'
            }} />
          </div>
          <div className="modal-details">
            <h1 className="modal-title">{currentMovie.title}</h1>
            <p className="modal-tagline">
              {currentMovie.tagline || t('unforgettable')}
            </p>
            
            <div className="modal-meta">
              <div className="meta-item" id="metaYear">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span id="modalYear">
                  {currentMovie.release_date ? new Date(currentMovie.release_date).getFullYear() : 'N/A'}
                </span>
              </div>
              <div className="meta-item" id="metaRuntime">
                <FontAwesomeIcon icon={faClock} />
                <span id="modalRuntime">
                  {currentMovie.runtime} min
                </span>
              </div>
              <div className="meta-item" id="metaSeasons" style={{ display: 'none' }}>
                <FontAwesomeIcon icon={faTv} />
                <span id="modalSeasons"></span>
              </div>
              <div className="meta-item">
                <FontAwesomeIcon icon={faGlobe} />
                <span id="modalLanguage">
                  {getLanguageName(currentMovie.original_language)}
                </span>
              </div>
              <div className="meta-item" id="metaStatus" style={{ display: 'none' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
                <span id="modalStatus"></span>
              </div>
            </div>
            
            <div className="modal-rating">
              <div className="rating-value" id="modalRating">
                {currentMovie.vote_average ? currentMovie.vote_average.toFixed(1) : 'N/A'}
              </div>
              <div className="rating-count" id="modalVoteCount">
                {t('votes', currentMovie.vote_count || 0)}
              </div>
            </div>
            
            <div className="modal-genres" id="modalGenres">
              {currentMovie.genres?.map(genre => (
                <div key={genre.id} className="genre">{genre.name}</div>
              ))}
            </div>
            
            <p className="modal-overview" id="modalOverview">
              {currentMovie.overview || t('noOverview')}
            </p>
            
            {collection && !showCollection && (
              <div 
                className={`collection-section capsule ${showBlinkingEffect ? 'blinking' : ''}`}
                onClick={handleCollectionClick}
                style={{
                  marginBottom: '20px',
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0' }}>
                  <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '10px', color: 'rgb(53, 229, 9)' }} />
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('partOfCollection')}</h3>
                </div>
              </div>
            )}
            
            {collection && showCollection && (
              <div className="collection-carousel" style={{
                marginBottom: '30px',
                borderRadius: '8px',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '10px', color: 'rgb(53, 229, 9)' }} />
                    <h3 style={{ margin: 0 }}>{collection.name}</h3>
                  </div>
                </div>

                <div style={{
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '20px',
                  marginTop: '-15px'
                }}>
                  "{t('collectionDescription')}"
                </div>
                
                <div className="collection-carousel-container" style={{ position: 'relative' }}>
                  {canScrollLeft && (
                    <button 
                      className="carousel-arrow left"
                      onClick={() => scrollCollection('prev')}
                      style={{
                        position: 'absolute',
                        left: '-20px',
                        top: window.innerWidth <= 768 ? '80px' : '110px',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        color: 'white',
                        cursor: 'pointer',
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                  )}
                  
                  <div 
                    className="collection-items"
                    ref={collectionCarouselRef}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${itemsPerPage}, 1fr)`,
                      gap: '15px',
                      overflow: 'hidden'
                    }}
                  >
                    {collectionItems.map((movie, index) => {
                      const moviePoster = movie.poster_path 
                        ? `https://image.tmdb.org/t/p/w300${movie.poster_path}`
                        : 'https://via.placeholder.com/300x450?text=No+Poster';
                      
                      const isVisible = index >= collectionScrollIndex * itemsPerPage && 
                                      index < (collectionScrollIndex + 1) * itemsPerPage;
                      
                      return (
                        <div 
                          key={movie.id}
                          className="collection-item"
                          onClick={() => handleCollectionItemClick(movie)}
                          style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                            position: 'relative',
                            display: isVisible ? 'block' : 'none',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <img 
                            src={moviePoster} 
                            alt={movie.title}
                            style={{
                              width: '100%',
                              height: 'auto',
                              aspectRatio: '2/3',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '10px',
                              border: movie.id === currentMovie.id ? '3px solid #ec087aefdc' : 'none'
                            }}
                          />
                          <div style={{
                            fontWeight: 'bold',
                            marginBottom: '5px',
                            fontSize: isMobile ? '0.9rem' : '1rem'
                          }}>
                            {movie.title}
                          </div>
                          <div style={{
                            color: '#aaa',
                            fontSize: '0.9rem',
                            marginBottom: '5px'
                          }}>
                            {movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A'}
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '3px',
                            marginBottom: '5px'
                          }}>
                            {createStarRating(movie.vote_average, 'small')}
                          </div>
                          {movie.id === currentMovie.id && (
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              right: 'auto',
                              background: 'linear-gradient(45deg, #1cb608e0, #037509dc)',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              boxShadow: '0 8px 10px rgba(0,0,0,0.3)' 
                            }}>
                              {t('youAreHere')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {canScrollRight && (
                    <button 
                      className="carousel-arrow right"
                      onClick={() => scrollCollection('next')}
                      style={{
                        position: 'absolute',
                        right: '-20px',
                        top: window.innerWidth <= 768 ? '80px' : '110px',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        color: 'white',
                        cursor: 'pointer',
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  )}
                </div>
              </div>
            )}
            
            <div className="modal-actions-container">
              <div className="modal-actions">
                <button 
                  className={`action-btn trailer-btn ${!trailerKey ? 'disabled' : ''}`} 
                  id="trailerBtn"
                  onClick={() => trailerKey && setShowTrailerModal(true)}
                  disabled={!trailerKey}
                >
                  <FontAwesomeIcon icon={faPlay} />
                  {trailerKey ? "Watch Trailer" : "Trailer Not Available"}
                </button>
                <button 
                  className={`action-btn add-btn ${isAdded ? 'added' : ''}`} 
                  onClick={handleAddToWatchlist}
                >
                  <FontAwesomeIcon icon={isAdded ? faCheck : faPlus} />
                  {isAdded ? "In Your List" : "Add to Watchlist"}
                </button>
              </div>
              
              <div className="modal-actions-extra">
                <Link className="action-btn action-btn-pink btn-play" to={`/watch/${currentMovie.id}`} onClick={handlePlayClick}>
                  <FontAwesomeIcon icon={faPlay} />
                  <span>Play</span>
                </Link>
                <Link className="action-btn action-btn-gray btn-play" to={`/alt-watch/${currentMovie.id}`} onClick={handlePlayClick}>
                  <FontAwesomeIcon icon={faPlay} />
                  <span>Play 2</span>
                </Link>
                <Link className="action-btn action-btn-gray btn-play" to={`/alt-watch-two/${currentMovie.id}`} onClick={handlePlayClick}>
                  <FontAwesomeIcon icon={faPlay} />
                  <span>Play 3</span>
                </Link>
              </div>
            </div>
            
            <div className="modal-cast">
              <h3 className="cast-title">{t('topCast')}</h3>
              <div 
                className="cast-grid" 
                id="castGrid" 
                style={{ 
                  display: 'grid',
                  gridTemplateColumns: `repeat(${isMobile ? 4 : 5}, 1fr)`,
                  gap: '15px',
                  marginBottom: '30px'
                }}
              >
                {credits?.cast?.slice(0, topCastCount).map(person => {
                  const photoPath = person.profile_path 
                    ? `https://image.tmdb.org/t/p/w200${person.profile_path}`
                    : actorFallbackImage;
                  
                  return (
                    <div 
                      className="cast-member" 
                      key={person.credit_id}
                      onClick={() => handleActorClick(person)}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                    >
                      <div style={{
                        position: 'relative',
                        width: isMobile ? '70px' : '100px',
                        height: isMobile ? '70px' : '100px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        margin: '0 auto 8px',
                        border: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <img 
                          src={photoPath} 
                          alt={person.name} 
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = actorFallbackImage;
                          }}
                        />
                      </div>
                      <div className="cast-name" style={{ 
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        {formatActorName(person.name)}
                      </div>
                      <div className="cast-character" style={{ 
                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                        color: '#aaa'
                      }}>
                        {person.character}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <h3 className="cast-title">{t('supportingCast')}</h3>
              <div 
                className="cast-grid" 
                id="supportingCastGrid"
                style={{ 
                  display: 'grid',
                  gridTemplateColumns: `repeat(${isMobile ? 4 : 5}, 1fr)`,
                  gap: '15px'
                }}
              >
                {credits?.cast?.slice(topCastCount, topCastCount + supportingCastCount).map(person => {
                  const photoPath = person.profile_path 
                    ? `https://image.tmdb.org/t/p/w200${person.profile_path}`
                    : actorFallbackImage;
                  
                  return (
                    <div 
                      className="cast-member" 
                      key={person.credit_id}
                      onClick={() => handleActorClick(person)}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                    >
                      <div style={{
                        position: 'relative',
                        width: isMobile ? '70px' : '100px',
                        height: isMobile ? '70px' : '100px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        margin: '0 auto 8px',
                        border: '2px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <img 
                          src={photoPath} 
                          alt={person.name} 
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = actorFallbackImage;
                          }}
                        />
                      </div>
                      <div className="cast-name" style={{ 
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        {formatActorName(person.name)}
                      </div>
                      <div className="cast-character" style={{ 
                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                        color: '#aaa'
                      }}>
                        {person.character}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {recMoviesToShow.length > 0 && (
          <div className="recommended-movies" style={{ 
            padding: '20px 20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            marginTop: 'auto',
          }}>
            <h3 className="recommended-title">{t('similarMovies')}</h3>
            <div className="recommended-grid" style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${isMobile ? 2 : 5}, 1fr)`,
              gap: '15px',
              marginTop: '20px'
            }}>
              {recMoviesToShow.map(recMovie => {
                const recPosterPath = recMovie.poster_path 
                  ? `https://image.tmdb.org/t/p/w500${recMovie.poster_path}`
                  : 'https://via.placeholder.com/500x750?text=No+Poster';
                
                return (
                  <div 
                    className="recommended-movie" 
                    key={recMovie.id}
                    onClick={() => handleRecommendationClick(recMovie)}
                    style={{ 
                      cursor: isRateLimited ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                      opacity: isRateLimited ? 0.7 : 1
                    }}
                  >
                    <img 
                      src={recPosterPath} 
                      alt={recMovie.title} 
                      style={{ 
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '2/3',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        marginBottom: '10px'
                      }}
                    />
                    <div className="recommended-movie-title" style={{
                      fontWeight: 'bold',
                      marginBottom: '5px',
                      fontSize: '0.9rem'
                    }}>
                      {recMovie.title}
                    </div>
                    <div className="star-rating" style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '3px'
                    }}>
                      {createStarRating(recMovie.vote_average, 'small')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {trailerKey && (
        <TrailerModal 
          isOpen={showTrailerModal}
          onClose={() => setShowTrailerModal(false)}
          youtubeKey={trailerKey}
        />
      )}
      
      {selectedTVShow && (
        <SeriesModal
          isOpen={true}
          onClose={handleCloseTVModal}
          onBack={handleCloseTVModal}
          seriesId={selectedTVShow}
          onUpdate={onUpdate}
          onOpenMovie={(movieId) => {
            setSelectedTVShow(null);
            setCurrentMovie({ id: movieId, media_type: 'movie' });
          }}
          history={localHistory}
        />
      )}
      
      {selectedActor && (
        <div className="actor-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backdropPath})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(10px)',
            zIndex: -1
          }}></div>
          
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: -1
          }}></div>
          
          <div 
            className="actor-modal" 
            ref={actorModalRef}
            style={{
              width: isMobile ? '100%' : '80%',
              maxWidth: isMobile ? 'none' : '1200px',
              backgroundColor: 'transparent',
              borderRadius: isMobile ? '0' : '10px',
              padding: isMobile ? '15px' : '30px',
              position: 'relative',
              maxHeight: isMobile ? '100vh' : '90vh',
              overflowY: 'auto',
              color: 'white',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <style>
              {`
                .actor-modal::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            
            <div className="close-actor-modal" onClick={handleCloseActorModal} style={{
              position: 'absolute',
              top: isMobile ? '15px' : '20px',
              right: isMobile ? '15px' : '20px',
              background: 'rgba(255, 255, 255, 0.15)',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 10,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <FontAwesomeIcon icon={faTimes} style={{ color: 'white', fontSize: '18px' }} />
            </div>
            
            <div className="actor-header" style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              marginBottom: '30px',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <div style={{
                position: 'relative',
                width: isMobile ? '120px' : '150px',
                height: isMobile ? '120px' : '150px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: isMobile ? '15px' : 0,
                marginRight: isMobile ? 0 : '30px',
                border: '3px solid rgba(255, 255, 255, 0.2)'
              }}>
                <img 
                  src={selectedActor.profile_path 
                    ? `https://image.tmdb.org/t/p/w300${selectedActor.profile_path}` 
                    : actorFallbackImage} 
                  alt={selectedActor.name} 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = actorFallbackImage;
                  }}
                />
              </div>
              <div>
                <h2 style={{ 
                  fontSize: isMobile ? '1.5rem' : '2rem', 
                  marginBottom: '10px',
                  marginTop: isMobile ? '10px' : 0 
                }}>
                  {selectedActor.name}
                </h2>
                <p style={{ color: '#aaa', fontSize: '1.1rem' }}>
                  {selectedActor.known_for_department}
                </p>
              </div>
            </div>
            
            <div className="content-filter2" style={{
              display: 'flex',
              background: 'rgba(30, 30, 30, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '30px',
              padding: '0',
              margin: '20px 0 30px',
              width: '100%',
              maxWidth: '300px',
              height: '50px',
              overflow: 'hidden',
              position: 'relative',
              marginLeft: isMobile ? 'auto' : '0',
              marginRight: isMobile ? 'auto' : '0'
            }}>
              <button 
                className={`filter-btn2 ${contentFilter === 'movies' ? 'active' : ''}`}
                onClick={() => setContentFilter('movies')}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: contentFilter === 'movies' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  background: contentFilter === 'movies' ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
                }}
              >
                {t('movies')}
              </button>
              <div className="filter-divider2" style={{
                width: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                height: '100%'
              }}></div>
              <button 
                className={`filter-btn2 ${contentFilter === 'tv' ? 'active' : ''}`}
                onClick={() => setContentFilter('tv')}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: contentFilter === 'tv' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  background: contentFilter === 'tv' ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
                }}
              >
                {t('tvShows')}
              </button>
            </div>
            
            <div className="filmography-section">
              <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', textAlign: isMobile ? 'center' : 'left' }}>
                {t('filmography')}
              </h3>
              
              {actorFilmography ? (
                <div className="filmography-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '20px'
                }}>
                  {filteredFilmography().length > 0 ? (
                    filteredFilmography().map(item => (
                      <div 
                        key={item.credit_id}
                        style={{ 
                          textAlign: 'center', 
                          cursor: isRateLimited ? 'not-allowed' : 'pointer',
                          opacity: isRateLimited ? 0.7 : 1
                        }}
                        onClick={() => !isRateLimited && handleFilmographyItemClick(item)}
                      >
                        <img 
                          src={`https://image.tmdb.org/t/p/w200${item.poster_path}`}
                          alt={item.title || item.name} 
                          style={{
                            width: '100%',
                            height: 'auto',
                            aspectRatio: '2/3',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            marginBottom: '10px'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/200x300?text=No+Poster';
                          }}
                        />
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '0.9rem' }}>
                          {item.title || item.name}
                        </div>
                        <div style={{ color: '#aaa', fontSize: '0.8rem', marginBottom: '5px' }}>
                          {item.release_date ? new Date(item.release_date).getFullYear() : 
                           item.first_air_date ? new Date(item.first_air_date).getFullYear() : 'N/A'}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '2px' }}>
                          {createStarRating(item.vote_average, 'small')}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ 
                      gridColumn: '1 / -1', 
                      textAlign: 'center', 
                      padding: '20px',
                      color: '#aaa'
                    }}>
                      {t('noFilmography', contentFilter === 'movies' ? t('movies').toLowerCase() : t('tvShows').toLowerCase())}
                    </div>
                  )}
                </div>
              ) : (
                <p>{t('loadingFilmography')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MoviesModal.propTypes = {
  movie: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  onBack: PropTypes.func,
  history: PropTypes.array
};

export default MoviesModal;